import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../../../../apps/kaboom-ui/src/environments/environment';
import { NavBadgeService } from './nav-badge.service';

/**
 * Maintains a SignalR connection to the communications hub and translates
 * real-time events into nav-badge state. Currently: a new application for the
 * org flips the Candidates tab's "unseen" marker (red dot).
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly auth   = inject(AuthService);
  private readonly badges = inject(NavBadgeService);

  private connection?: HubConnection;

  private readonly applicationReceived         = new Subject<void>();
  private readonly backfillOpportunityReceived = new Subject<void>();

  /** Fires when the org receives a new application. Grids can subscribe to live-refresh. */
  readonly applicationReceived$: Observable<void> = this.applicationReceived.asObservable();
  /** Fires when a new backfill opportunity is detected for the org. */
  readonly backfillOpportunityReceived$: Observable<void> = this.backfillOpportunityReceived.asObservable();

  /** Opens the SignalR connection. Idempotent — repeated calls are no-ops. */
  async start(): Promise<void> {
    if (this.connection) return;

    const hubUrl = `${environment.apiUrl}/api/communications/hubs/notifications`;

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        // Bearer token via Auth0 — no cookies, so withCredentials stays off.
        accessTokenFactory: () =>
          firstValueFrom(
            this.auth.getAccessTokenSilently({
              authorizationParams: {
                audience: environment.auth0.authorizationParams.audience,
              },
            }),
          ),
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // Server broadcasts this to the caller's org group when a CV becomes an application.
    this.connection.on('ApplicationReceived', () => {
      this.badges.markUnseen('candidates');
      this.applicationReceived.next();
    });

    // Server broadcasts this when a new backfill opportunity is automatically created.
    this.connection.on('BackfillOpportunityReceived', () => {
      this.badges.markUnseen('backfill');
      this.backfillOpportunityReceived.next();
    });

    try {
      await this.connection.start();
    } catch (err) {
      // Non-fatal — notifications are a nice-to-have, never block the app.
      console.error('[Notifications] SignalR connection failed', err);
      this.connection = undefined;
    }
  }

  async stop(): Promise<void> {
    if (
      this.connection &&
      this.connection.state !== HubConnectionState.Disconnected
    ) {
      await this.connection.stop();
    }
    this.connection = undefined;
  }
}
